/**
 * What a part's spanners and tuplets look like once flattened onto its events.
 *
 * The document holds a slur as one object naming the two events it joins; MusicXML holds
 * it as a mark on each of those notes, paired by a small number that has to be free for
 * the whole time the slur is open. Two slurs may share a number only if they do not
 * overlap, so the numbers are handed out against the part's own event order rather than
 * taken from the spanner's position in the list -- which is arbitrary and would put two
 * overlapping slurs on the same number, joining the wrong ends.
 *
 * Tuplets are the same shape of problem in miniature: the document marks the ratio on
 * every event in the group, the format marks only where the bracket opens and closes.
 */

import type { EventId, MeasureId, SpannerId } from "../document/ids";
import type { TupletRatio } from "../document/duration";
import { isOctaveShift } from "../document/types";
import type { Part, Score, Spanner, SpannerKind } from "../document/types";
import { accidentalPlan } from "./export-accidentals";
import type { AccidentalPlan } from "./export-accidentals";
import { beamMarks } from "./export-beams";
import type { BeamMark } from "./export-beams";
import type { GlissMark, SlurMark, TupletMark } from "./export-notes";

/**
 * A line standing beside the notes rather than on them, at the event one of its ends binds
 * to: a hairpin as `<wedge>`, a pedal as `<pedal>`, an octave sign as `<octave-shift>`.
 *
 * They are planned together because they are placed together -- the start before the note
 * it opens on, the stop after the note it closes on -- and told apart only when the element
 * is written. A stop carries its kind as well as its number, since the three are different
 * elements and a reader pairs ends within one of them.
 */
export interface HeldMark {
  readonly kind: Exclude<SpannerKind, "slur" | "glissando">;
  readonly number: number;
  readonly placement: "above" | "below";
}

/** Everything about a part that has to be known before any of its bars can be written. */
export interface PartPlan {
  /** Slur marks to put in a note's `<notations>`, by the event they land on. */
  readonly slurs: ReadonlyMap<EventId, readonly SlurMark[]>;
  /** Glissando marks, which go in the same `<notations>` a slur does. */
  readonly glissandos: ReadonlyMap<EventId, readonly GlissMark[]>;
  /** Held lines opening at an event, written as a `<direction>` before it. */
  readonly heldStarts: ReadonlyMap<EventId, readonly HeldMark[]>;
  /** Held lines closing at an event, written as a `<direction>` after it. */
  readonly heldStops: ReadonlyMap<EventId, readonly HeldMark[]>;
  readonly tuplets: ReadonlyMap<EventId, TupletMark>;
  /**
   * The accidental each note head prints, for the heads that left it open.
   *
   * Worked out here rather than left to the reader because readers disagree about it and
   * the one this package renders through never prints a natural; see `export-accidentals.ts`.
   */
  readonly accidentals: AccidentalPlan;
  /**
   * Beams worked out for the notes that left the choice open, empty unless asked for.
   *
   * See `export-beams.ts` for why this is derived on the way out rather than held in the
   * document, and why a caller has to ask.
   */
  readonly beams: ReadonlyMap<EventId, readonly BeamMark[]>;
}

function push<K, V>(into: Map<K, V[]>, key: K, value: V): void {
  const existing = into.get(key);
  if (existing === undefined) into.set(key, [value]);
  else existing.push(value);
}

/** Every event of a part, numbered in playing order, so spans can be compared. */
function eventOrder(score: Score, part: Part): ReadonlyMap<EventId, number> {
  const order = new Map<EventId, number>();
  let at = 0;
  for (const global of score.measures) {
    const measure = part.measures[global.id];
    if (measure === undefined) continue;
    for (const voice of measure.voices) {
      for (const event of voice.events) {
        if (!order.has(event.id)) order.set(event.id, at);
        at += 1;
      }
    }
  }
  return order;
}

/** MusicXML pairs the ends of a slur or a wedge by a number from one to six. */
const MAX_NUMBER = 6;

/**
 * The lowest number free for the whole length of each span.
 *
 * Numbering by the spanner's position in the part's list instead would give two
 * overlapping slurs the same number, and a reader pairing ends by number would then join
 * the start of one to the stop of the other.
 */
function assignNumbers(
  spanners: readonly Spanner[],
  order: ReadonlyMap<EventId, number>,
): ReadonlyMap<SpannerId, number> {
  const spans = spanners
    .map((spanner) => ({
      spanner,
      from: order.get(spanner.from) ?? 0,
      to: order.get(spanner.to) ?? 0,
    }))
    .sort((a, b) => a.from - b.from);

  const freeAt: number[] = [];
  const numbers = new Map<SpannerId, number>();

  for (const span of spans) {
    let number = freeAt.findIndex((busyUntil) => busyUntil < span.from) + 1;
    if (number === 0) number = Math.min(freeAt.length + 1, MAX_NUMBER);
    freeAt[number - 1] = Math.max(span.to, span.from);
    numbers.set(span.spanner.id, number);
  }

  return numbers;
}

function sameRatio(a: TupletRatio | null, b: TupletRatio | null): boolean {
  if (a === null || b === null) return a === b;
  return a.actual === b.actual && a.normal === b.normal;
}

/** Where each tuplet bracket opens and closes, looking only within one voice of one bar. */
function tupletMarks(part: Part, ids: readonly MeasureId[]): ReadonlyMap<EventId, TupletMark> {
  const marks = new Map<EventId, TupletMark>();
  for (const id of ids) {
    const measure = part.measures[id];
    if (measure === undefined) continue;
    for (const voice of measure.voices) {
      voice.events.forEach((event, index) => {
        const ratio = event.duration.tuplet;
        if (ratio === null) return;
        const before = voice.events[index - 1]?.duration.tuplet ?? null;
        const after = voice.events[index + 1]?.duration.tuplet ?? null;
        if (!sameRatio(before, ratio)) marks.set(event.id, "start");
        else if (!sameRatio(after, ratio)) marks.set(event.id, "stop");
      });
    }
  }
  return marks;
}

/**
 * Which pool a line's number comes from.
 *
 * MusicXML numbers each kind of line in its own space, so a slur and a wedge may both be
 * number 1 without their ends being confused. Sharing one pool across all of them would
 * still read back correctly but would run out of numbers -- there are only six -- on a
 * passage that is slurred, swelling and under an octave sign at once.
 */
function numberPool(kind: SpannerKind): string {
  if (kind === "slur" || kind === "glissando" || kind === "pedal") return kind;
  return isOctaveShift(kind) ? "octave" : "wedge";
}

/** Flatten one part's spanners and tuplets onto the events that carry them. */
export function planPart(score: Score, part: Part, beams: boolean): PartPlan {
  const order = eventOrder(score, part);
  const pools = new Map<string, ReadonlyMap<SpannerId, number>>();
  for (const spanner of part.spanners) {
    const pool = numberPool(spanner.kind);
    if (pools.has(pool)) continue;
    pools.set(
      pool,
      assignNumbers(
        part.spanners.filter((candidate) => numberPool(candidate.kind) === pool),
        order,
      ),
    );
  }
  const numberOf = (spanner: Spanner): number =>
    pools.get(numberPool(spanner.kind))?.get(spanner.id) ?? 1;

  const slurs = new Map<EventId, SlurMark[]>();
  const glissandos = new Map<EventId, GlissMark[]>();
  const heldStarts = new Map<EventId, HeldMark[]>();
  const heldStops = new Map<EventId, HeldMark[]>();

  for (const spanner of part.spanners) {
    const number = numberOf(spanner);
    if (spanner.kind === "slur") {
      push(slurs, spanner.from, { type: "start", number, placement: spanner.placement });
      push(slurs, spanner.to, { type: "stop", number, placement: spanner.placement });
      continue;
    }
    if (spanner.kind === "glissando") {
      push(glissandos, spanner.from, { type: "start", number });
      push(glissandos, spanner.to, { type: "stop", number });
      continue;
    }
    const mark: HeldMark = { kind: spanner.kind, number, placement: spanner.placement };
    push(heldStarts, spanner.from, mark);
    push(heldStops, spanner.to, mark);
  }

  return {
    slurs,
    glissandos,
    heldStarts,
    heldStops,
    tuplets: tupletMarks(
      part,
      score.measures.map((measure) => measure.id),
    ),
    accidentals: accidentalPlan(score, part),
    beams: beams ? beamMarks(score, part) : new Map(),
  };
}
